import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";
import WaitlistModal from "@/components/WaitlistModal";
import { isProduction } from "@/lib/platform";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  panelAlt: "#103532",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = ({ withTagline = true }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <span
      className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}
    >
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    {withTagline && (
      <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Funder</span>
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Readiness</span>
      </div>
    )}
  </div>
);

const OPTIONS = [
  {
    id: "pitch",
    name: "Pitch Deck",
    status: "live",
    headline: "Tell your story so well that you earn the next meeting.",
    bullets: [
      "AI-drafted, founder-tone narrative per slide",
      "11-slide Clinq Methodology · 5 pillars",
      "Live failure-registry & .pptx export",
    ],
    founder: "Forces you to answer the hard questions before someone else asks — sharpens every conversation, not just investor ones.",
    cta: "Start building",
  },
  {
    id: "model",
    name: "Financial Model",
    status: "live",
    headline: "A bespoke 3+5 year forecast that survives funder scrutiny.",
    bullets: [
      "Assumptions tailored to your stage and sector",
      "Monthly P&L, balance sheet and cash flow",
      "Live AI co-pilot · Excel export",
    ],
    founder: "Tests your business math and warns you before cash runs out — decide on paper before you risk real money.",
    cta: "Start building",
  },
  {
    id: "accounts",
    name: "Management Accounts",
    status: "live",
    headline: "Prove the business you pitched is the one you actually run.",
    bullets: [
      "Monthly board pack with actuals vs budget",
      "Clean revenue, costs and cash position",
      "Auto-reconciled and board-ready",
    ],
    founder: "Your monthly report card. Catch problems while you can still act — not at year-end when it's too late.",
    cta: "Start building",
  },
  {
    id: "valuation",
    name: "Business Valuation",
    status: "live",
    headline: "Triangulated. Defensible. Funder-grade.",
    bullets: [
      "Methodology v1 · 6 methods · 3-value output",
      "African market discount + risk premium build-up",
      "Auditable report you can hand to investors",
    ],
    founder: "Know what your business is really worth before you dilute — for fundraising, buy-outs, or just your own clarity.",
    cta: "Start valuation",
  },
];

/* Small SVG illustrations per card */
const PitchIcon = () => (
  <svg viewBox="0 0 80 60" className="w-full h-auto">
    <rect x="6" y="14" width="44" height="34" rx="3" fill={C.panelAlt} stroke={C.teal} strokeWidth="1" opacity="0.6" />
    <rect x="14" y="10" width="44" height="34" rx="3" fill={C.panelAlt} stroke={C.teal} strokeWidth="1" opacity="0.85" />
    <rect x="22" y="6" width="50" height="40" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1.5" />
    <rect x="26" y="12" width="22" height="4" rx="1" fill={C.teal} />
    <rect x="26" y="20" width="34" height="2.5" rx="1" fill="#fff" opacity="0.3" />
    <rect x="26" y="26" width="30" height="2.5" rx="1" fill="#fff" opacity="0.3" />
    <rect x="26" y="32" width="34" height="2.5" rx="1" fill="#fff" opacity="0.3" />
    <circle cx="62" cy="40" r="4" fill={C.orange} />
  </svg>
);
const ModelIcon = () => (
  <svg viewBox="0 0 80 60" className="w-full h-auto">
    <rect x="6" y="6" width="68" height="48" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1.5" />
    <rect x="6" y="6" width="68" height="9" rx="3" fill={C.teal} opacity="0.25" />
    <line x1="22" y1="6" x2="22" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
    <line x1="38" y1="6" x2="38" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
    <line x1="54" y1="6" x2="54" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
    <line x1="6" y1="22" x2="74" y2="22" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
    <line x1="6" y1="35" x2="74" y2="35" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
    <path d="M12 48 L26 38 L42 30 L58 22 L70 14" stroke={C.orange} strokeWidth="2" fill="none" strokeLinecap="round" />
    <circle cx="70" cy="14" r="2.5" fill={C.orange} />
  </svg>
);
const AccountsIcon = () => (
  <svg viewBox="0 0 80 60" className="w-full h-auto">
    <rect x="10" y="4" width="56" height="52" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1.5" />
    <rect x="14" y="10" width="48" height="6" rx="1.5" fill={C.teal} opacity="0.3" />
    <rect x="14" y="10" width="24" height="6" rx="1.5" fill={C.teal} />
    <rect x="14" y="20" width="48" height="3" rx="1" fill="#fff" opacity="0.25" />
    <rect x="14" y="20" width="36" height="3" rx="1" fill={C.teal} opacity="0.7" />
    <rect x="14" y="27" width="48" height="3" rx="1" fill="#fff" opacity="0.25" />
    <rect x="14" y="27" width="28" height="3" rx="1" fill={C.teal} opacity="0.7" />
    <rect x="14" y="34" width="48" height="3" rx="1" fill="#fff" opacity="0.25" />
    <rect x="14" y="34" width="18" height="3" rx="1" fill={C.orange} />
    <circle cx="56" cy="46" r="6" fill={C.teal} />
    <path d="M53 46 l2 2 l4 -4" stroke="#000" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ValuationIcon = () => (
  <svg viewBox="0 0 80 60" className="w-full h-auto">
    <rect x="10" y="6" width="60" height="48" rx="3" fill={C.panel} stroke={C.orange} strokeWidth="1.5" />
    <rect x="16" y="14" width="18" height="6" rx="1" fill={C.orange} opacity="0.4" />
    <rect x="16" y="24" width="48" height="2.5" rx="1" fill="#fff" opacity="0.25" />
    <rect x="16" y="30" width="40" height="2.5" rx="1" fill="#fff" opacity="0.25" />
    <rect x="16" y="36" width="44" height="2.5" rx="1" fill="#fff" opacity="0.25" />
    <rect x="20" y="42" width="12" height="8" rx="1" fill={C.teal} opacity="0.6" />
    <rect x="34" y="42" width="12" height="8" rx="1" fill={C.orange} />
    <rect x="48" y="42" width="12" height="8" rx="1" fill={C.teal} opacity="0.6" />
  </svg>
);
const ICONS = { pitch: <PitchIcon />, model: <ModelIcon />, accounts: <AccountsIcon />, valuation: <ValuationIcon /> };

const TYPE_SEO = {
  pitch: {
    title: "Build your pitch deck",
    description: "Start a funder-ready pitch deck with the Clinq Methodology: AI-drafted narrative, 5-pillar scoring, the 10-failure registry, and one-click PowerPoint export.",
    eyebrow: "Pitch Deck Builder",
    h1Tail: "pitch deck",
    sub: "Score every slide against the 5 Clinq pillars and the 10-failure registry funders apply BEFORE saying yes. AI-drafted, founder-tone, exported to PowerPoint.",
  },
  model: {
    title: "Build your financial model",
    description: "Build a bespoke 3+5-year financial model with assumption depth tailored to your stage — monthly P&L, balance sheet, cashflow, and an AI co-pilot reviewing as you go.",
    eyebrow: "Financial Model Builder",
    h1Tail: "financial model",
    sub: "Pre-revenue founders get a lean unit-economics model. Series A+ founders get cohort retention, capital event timeline and DSCR cover. Monthly P&L, balance sheet, cashflow.",
  },
  accounts: {
    title: "Build your management accounts",
    description: "Turn your Excel ledger, bank statement and M-Pesa export into a board-ready monthly P&L, balance sheet and cashflow — reconciled against the Clinq Golden Rule.",
    eyebrow: "Management Accounts",
    h1Tail: "management accounts",
    sub: "The Clinq 7-step pipeline (Collect → Clean → Reconcile → Classify → P&L → BS → Cashflow) turns messy data into a board pack that survives funder due diligence.",
  },
};

const DEFAULT_SEO = {
  title: "Start building",
  description: "Pick what you want to build first: a pitch deck, a financial model, or management accounts — all tailored to your business stage and industry.",
  eyebrow: "Step 1 of 2",
  h1Tail: "build?",
  sub: "Pick the document you need first. You can always come back for the other two — most funders want all three.",
};

export default function BuildStart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(params.get("type") || null);
  // Production shortlist gate — Management Accounts / Business Valuation
  // still get selected + previewed, but hitting the final CTA opens a
  // waitlist modal instead of routing into the builder.
  const locked = isProduction();
  const [waitService, setWaitService] = useState(null);

  useEffect(() => {
    const t = params.get("type");
    if (t && OPTIONS.find((o) => o.id === t)) setSelected(t);
  }, [params]);

  const handlePick = (opt) => {
    setSelected(opt.id);
    if (opt.id === "pitch") {
      navigate(user ? "/pitch/new" : "/signup?next=/pitch/new");
      return;
    }
    if (opt.id === "model") {
      navigate(user ? "/fm/new" : "/signup?next=/fm/new");
      return;
    }
    // Accounts + Valuation are locked in production — funnel to the shortlist.
    if (locked) {
      const serviceMap = { accounts: "accounts", valuation: "business_valuation" };
      setWaitService(serviceMap[opt.id] || "general");
      return;
    }
    if (opt.id === "accounts") {
      navigate(user ? "/mgmt/new" : "/signup?next=/mgmt/new");
      return;
    }
    if (opt.id === "valuation") {
      navigate(user ? "/valuation/new" : "/signup?next=/valuation/new");
      return;
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="build-start-page">
      <WaitlistModal open={!!waitService} service={waitService || "general"} onClose={() => setWaitService(null)} initialEmail={user?.email || ""} />
      {(() => {
        const t = params.get("type");
        const seo = TYPE_SEO[t] || DEFAULT_SEO;
        const path = t ? `/build/start?type=${t}` : "/build/start";
        return <SEO title={seo.title} description={seo.description} path={path} />;
      })()}
      {/* Header */}
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="back-to-landing"><ClinqLogo /></Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="header-dashboard"
                className="text-sm font-medium text-white/90 hover:text-white">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" data-testid="header-login" className="text-sm font-medium text-white/90 hover:text-white px-3 py-2">Log In</Link>
                <Link to="/signup" data-testid="header-signup"
                  className="text-sm font-semibold px-4 py-2 rounded-md transition-colors"
                  style={{ backgroundColor: C.teal, color: "#fff" }}>Sign Up</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full opacity-10 blur-3xl"
          style={{ background: `radial-gradient(circle, ${C.teal} 0%, transparent 60%)` }} aria-hidden="true"></div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-12">
          {(() => {
            const t = params.get("type");
            const hero = TYPE_SEO[t] || DEFAULT_SEO;
            const isDefault = !TYPE_SEO[t];
            return (
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: isDefault ? "rgba(255,255,255,0.6)" : C.tealSoft }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
                  {hero.eyebrow}
                </div>
                <h1 className="font-extrabold tracking-tighter mt-4 text-5xl sm:text-6xl leading-[1.02]"
                  style={{ letterSpacing: "-0.03em" }}>
                  {isDefault ? <>What do you want to <span style={{ color: C.teal }}>build?</span></>
                    : <>Build a funder-ready <span style={{ color: C.teal }}>{hero.h1Tail}</span>.</>}
                </h1>
                <p className="mt-5 text-lg text-white/65 leading-relaxed max-w-2xl">
                  {hero.sub}
                </p>
              </div>
            );
          })()}

          {/* Three options */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="build-options-grid">
            {OPTIONS.map((opt) => {
              const live = opt.status === "live";
              const isSelected = selected === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => handlePick(opt)}
                  data-testid={`build-option-${opt.id}`}
                  className={`group relative text-left rounded-2xl p-7 border-2 transition-all hover:-translate-y-1 ${
                    isSelected ? "scale-[1.02]" : ""
                  }`}
                  style={{
                    backgroundColor: C.panel,
                    borderColor: isSelected ? C.teal : "rgba(123, 211, 206, 0.15)",
                    boxShadow: isSelected ? `0 20px 60px -20px ${C.teal}55` : "none",
                  }}
                >
                  <div className="flex items-start justify-between mb-5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"
                      style={{
                        backgroundColor: live ? C.teal : "transparent",
                        color: live ? "#fff" : C.tealSoft,
                        border: live ? "none" : `1px solid ${C.teal}`,
                      }}
                      data-testid={`build-option-${opt.id}-status`}>
                      {live ? <><span className="w-1.5 h-1.5 rounded-full bg-white"></span> Live</>
                            : <><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span> Coming soon</>}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-white/30">
                      0{OPTIONS.indexOf(opt) + 1}
                    </span>
                  </div>

                  <div className="w-full max-w-[200px] mx-auto mb-6">{ICONS[opt.id]}</div>

                  <h3 className="font-extrabold text-2xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>{opt.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">{opt.headline}</p>

                  <ul className="mt-5 space-y-2">
                    {opt.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-white/60 leading-snug">
                        <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ backgroundColor: C.tealSoft }}></span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {opt.founder && (
                    <div className="mt-5 rounded-lg px-3 py-2.5 border-l-2"
                      style={{
                        backgroundColor: "rgba(245, 158, 11, 0.06)",
                        borderColor: C.orange,
                      }}
                      data-testid={`build-founder-caption-${opt.id}`}>
                      <div className="text-[9px] uppercase tracking-[0.22em] font-bold" style={{ color: C.orange }}>
                        Why you need it
                      </div>
                      <div className="mt-1.5 text-xs leading-relaxed text-white/70">
                        {opt.founder}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-5 border-t border-white/10">
                    <span
                      className="inline-flex items-center gap-2 text-sm font-semibold transition-transform group-hover:translate-x-1"
                      style={{ color: live ? C.teal : C.tealSoft }}>
                      {opt.cta} <ArrowRight size={14} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/" className="text-sm text-white/50 hover:text-white/80 transition-colors" data-testid="back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
